import type { PricingPlan } from "../api";
import type { LibraryCourse } from "@/features/dashboard/types";

/**
 * Per-plan ownership state driving the public course page visual stack
 * (plan-card badge, upgrade callout, enroll CTA).
 *
 * Legacy full-access (`hasFullAccessEntitlement`) splits across tier cards:
 * the nominal default tier (`pickLegacyEquivalentPlan`) keeps
 * `owned-legacy-full` with Continue + pill; other tier cards use
 * `owned-legacy-peer` (context chip only).
 */
export type OwnedState =
  | { kind: "not-owned" }
  | { kind: "owned-this" }
  | { kind: "owned-other"; upgradeable: boolean }
  /**
   * Legacy / full-access snapshot (see `hasFullAccessEntitlement`) on the
   * nominal default-tier card only — "Continue Learning" lives here.
   */
  | { kind: "owned-legacy-full" }
  /**
   * Same legacy entitlement, non-default tier cards — peer-style context only.
   */
  | { kind: "owned-legacy-peer" };

/** Inputs for `resolveOwnedState` (public course pricing surfaces). */
export type ResolveOwnedStateContext = {
  isOwned: boolean;
  isLegacyFullAccess: boolean;
  ownedPlanId: string | null | undefined;
  /**
   * Catalog row for `ownedPlanId` when that tier is still listed publicly.
   * When missing (e.g. archived plan filtered out of `/pricing-plans`), no
   * card resolves to `owned-this`; upgrade paths use `upgradeableTo` from this
   * object when present.
   */
  sourceTierPlan: PricingPlan | null;
  allPlans: PricingPlan[];
};

/**
 * Full-access ownership trigger for the public course page (docs/15 §8.3 +
 * §8.10). Matches the backend gating contract: the entitlement's
 * `accessScope` snapshot is the SINGLE source of truth for gating, and the
 * absence of a scope means full access — regardless of whether
 * `pricingPlanId` is also absent.
 *
 * Three distinct shapes all resolve to `true` here, and all three mean the
 * learner can open every lesson on the course:
 *
 *   1. Pure legacy                — pre-plan-layer enrollment; neither field set.
 *                                   (B1c backfill hasn't run against this row.)
 *   2. B1c-backfilled              — `pricingPlanId` stamped by
 *                                   `migrate-default-pricing-plans.ts`, scope
 *                                   intentionally left unset per docs/15 §8.14
 *                                   step 4 ("no retroactive restriction").
 *   3. Fresh grant / B4 upgrade    — the source plan's scope was `full` at
 *      into a `full`-scope plan    grant time, and §8.10 encodes `full` as
 *                                   absence-of-scope on the entitlement.
 *
 * Without (2) and (3) the sidebar would contradict the curriculum gate — the
 * learner would see no `<Lock />` icons but the pricing block would still try
 * to offer an "Upgrade to …" CTA on tiers they already effectively own.
 */
export function hasFullAccessEntitlement(
  // Accept the `pricingPlanId` field even though we don't read it: callers
  // thread their whole `LibraryCourse` entry here, and keeping the wider
  // shape avoids churn at every call site + lets test fixtures pin the
  // "pricingPlanId stamped but scope absent" shape explicitly.
  ownedCourse: Pick<LibraryCourse, "pricingPlanId" | "accessScope"> | null | undefined,
): boolean {
  if (!ownedCourse) return false;
  return ownedCourse.accessScope == null;
}

/**
 * Plan the library treats as the learner's "current" tier when `pricingPlanId`
 * is missing (legacy entitlement). Matches backend `getDefaultPlanForCourse` /
 * `resolveCurrentPlan` preference: explicit default, else lowest `sortOrder`
 * single-tier plan with full access.
 *
 * Only consumed by non-legacy-full flows today (upgrade-target resolution on
 * multi-tier post-migration). Left intact so the B4 upgrade pathway keeps
 * working unchanged.
 */
export function pickLegacyEquivalentPlan(plans: PricingPlan[]): PricingPlan | null {
  const defaults = plans.filter((p) => p.isDefault);
  if (defaults.length > 0) {
    return [...defaults].sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
  }
  const singles = plans.filter(
    (p) =>
      p.planType === "single" &&
      (p.accessScope?.type === "full" || p.accessScope === undefined),
  );
  if (singles.length === 0) return null;
  return [...singles].sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
}

export function getEffectiveOwnedPlan(
  plans: PricingPlan[],
  ownedPlanId: string | undefined | null,
): PricingPlan | null {
  if (ownedPlanId) {
    return plans.find((p) => p._id === ownedPlanId) ?? null;
  }
  const legacy = pickLegacyEquivalentPlan(plans);
  return legacy ?? plans[0] ?? null;
}

/**
 * Per-card ownership + upgrade eligibility. Legacy-full splits: the default
 * nominal tier (`pickLegacyEquivalentPlan`) keeps `owned-legacy-full`; other
 * tier cards use `owned-legacy-peer` (chip only, no duplicate Continue).
 *
 * Upgrade eligibility: `sourceTierPlan.upgradeableTo` must list **this**
 * card's plan id (matches backend B4 / library upgrade resolution).
 */
export function resolveOwnedState(
  plan: PricingPlan,
  ctx: ResolveOwnedStateContext,
): OwnedState {
  if (!ctx.isOwned) return { kind: "not-owned" };
  if (ctx.isLegacyFullAccess) {
    const resume = pickLegacyEquivalentPlan(ctx.allPlans);
    if (resume && plan._id === resume._id) return { kind: "owned-legacy-full" };
    return { kind: "owned-legacy-peer" };
  }

  if (ctx.ownedPlanId && ctx.ownedPlanId === plan._id) {
    return { kind: "owned-this" };
  }
  const upgradeable =
    !!ctx.sourceTierPlan &&
    !!ctx.ownedPlanId &&
    (ctx.sourceTierPlan.upgradeableTo?.includes(plan._id) ?? false);
  return { kind: "owned-other", upgradeable };
}

/**
 * Visual-contract helper for the plan card — decides BADGE vs PILL vs NOTHING
 * from a resolved `OwnedState`. Kept as a pure function (no React) so the
 * unit-test suite validates the decision matrix without pulling in
 * @testing-library/react. PlanCard consumes this directly so the decision
 * table can never drift between code and tests.
 *
 *   kind                 → badge: "mine"     → "Your plan" (emerald-100/700)
 *   kind: owned-legacy-  → badge: "included" → "Included in your access" (emerald-100/700)
 *     full
 *   owned-legacy-peer, owned-other, not-owned → badge: null
 *
 * `highlightCard` signals whether the card surface gets the emerald ring;
 * today that's true for both legacy-full and owned-this.
 */
export type PlanCardOwnedDisplay = {
  badge: "mine" | "included" | null;
  highlightCard: boolean;
};

export function resolvePlanCardOwnedDisplay(state: OwnedState): PlanCardOwnedDisplay {
  switch (state.kind) {
    case "owned-this":
      return { badge: "mine", highlightCard: true };
    case "owned-legacy-full":
      return { badge: "included", highlightCard: true };
    case "owned-legacy-peer":
    case "owned-other":
    case "not-owned":
      return { badge: null, highlightCard: false };
  }
}
